import { type Signal, useComputed } from "@suse-o11y/signal";
import React from "react";
import { useEvent as useOnEvent } from "react-use";

import type { Time } from "~/tools/date-time/time";
import { min } from "~/tools/min";
import { timeFromPx } from "~/tools/time-from-px";
import { timeToPx } from "~/tools/time-to-px";
import { useHandler } from "~/tools/use-handler";

import { useTimestampCursorState } from "../../TimestampCursor";
import { useTimelineContext, useTimelineRange } from "../_context";

import { TimelinePlayHead, type TimelinePlayHeadTheme } from "./TimelinePlayHead";
import { useSetTimestampCursor } from "./useSetTimestampCursor";

export interface TimelineHoverTimestampProps {
	x: Signal<number>;
	disableMarker: boolean;
	onMouseDown(time: Time, px: number): void;
	onMouseUp: React.Dispatch<Time>;
}

export const TimelineHoverTimestamp: React.FC<TimelineHoverTimestampProps> = ({
	x,
	disableMarker,
	onMouseDown,
	onMouseUp,
}) => {
	const { stageWidth, refreshInterval, formatTimestamp } = useTimelineContext();
	const timestampCursorState = useTimestampCursorState();

	const xValue = useComputed(x);

	const highlightedMarker = useComputed(
		(monitor) => (disableMarker ? undefined : timestampCursorState.getHighlightedMarker(monitor)),
		[disableMarker, timestampCursorState],
	);

	const theme = useComputed<TimelinePlayHeadTheme>((monitor) => {
		if (highlightedMarker != null) {
			return "red";
		}

		if (!disableMarker && timestampCursorState.isHotkeyPressed(monitor)) {
			return "purple";
		}

		return "default";
	});

	const [timelineStart, timelineEnd, now] = useTimelineRange({
		interval: min(300, refreshInterval),
		// if no marker his highlighted it is fine to floor for seconds because
		// it will only apply to time format
		floor: highlightedMarker == null ? "seconds" : "milliseconds",
	});

	const px =
		highlightedMarker == null
			? xValue
			: timeToPx({
					time: highlightedMarker,
					width: stageWidth,
					interval: { start: timelineStart, end: timelineEnd },
				});

	const time =
		highlightedMarker ??
		timeFromPx({
			px: xValue,
			width: stageWidth,
			interval: { start: timelineStart, end: timelineEnd },
		});

	useSetTimestampCursor(time);

	useOnEvent(
		"mousemove",
		useHandler((event: MouseEvent) => {
			x.write(event.clientX);
		}),
	);

	useOnEvent(
		"mousedown",
		useHandler(() => {
			onMouseDown(time, px);
		}),
	);

	useOnEvent(
		"mouseup",
		useHandler(() => {
			onMouseUp(time);
		}),
	);

	return (
		<TimelinePlayHead className="inset-y-0" theme={theme} x={px}>
			{formatTimestamp({ time, timelineStart, timelineEnd, now }, px)}
		</TimelinePlayHead>
	);
};
